import { useEffect, useMemo, useRef, useState } from "react";
import PlatformField from "/src/components/UI/Field/Field";
import Dropdown from "/src/components/UI/DropDown/DropDown";
import "../styles/callEvaluation.css";

// Destructive buttons ask for a second click within 3 seconds instead of a modal.
export function useTwoStepConfirm(action) {
    const [armed, setArmed] = useState(false);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const trigger = () => {
        if (!armed) {
            setArmed(true);
            timer.current = setTimeout(() => setArmed(false), 3000);
            return;
        }
        clearTimeout(timer.current);
        setArmed(false);
        action();
    };

    return [armed, trigger];
}

// Platform Field (label + control), with the pink asterisk used by the other forms.
export function Field({ label, required, children }) {
    return (
        <PlatformField
            title={label}
            label={<span>{label}{required && <span className="obligatory_field"> *</span>}</span>}
        >
            {children}
        </PlatformField>
    );
}

// Platform Dropdown fed with plain strings; the first entry clears the value.
export function SelectField({ name, value, options, placeholder, onChange }) {
    const choices = useMemo(
        () => [{ text: placeholder, value: "" }, ...options.map((option) => ({ text: option, value: option }))],
        [options, placeholder]
    );

    return (
        <div className="ce-dd">
            <Dropdown
                name={name}
                choices={choices}
                defaultChoice={value}
                width="100%"
                hideSelectItem={false}
                setValue={(_name, selected) => onChange(selected)}
            />
        </div>
    );
}

export function CardTitle({ icon, title, hint, right }) {
    return (
        <div className="ce-card__head">
            <span className="ce-card__icon"><i className={icon}></i></span>
            <div className="ce-card__titles">
                <h3>{title}</h3>
                {hint && <p>{hint}</p>}
            </div>
            {right && <div className="ce-card__right">{right}</div>}
        </div>
    );
}

// Circular gauge (SVG) - the live score in the notepad, and the average in the stats.
export function ScoreRing({ value, size = 168, strokeWidth = 12, color = "var(--main)", children }) {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const ratio = Math.max(0, Math.min(1, value || 0));

    return (
        <div className="ce-ring" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle className="ce-ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={strokeWidth} fill="none" />
                <circle
                    className="ce-ring__value"
                    cx={size / 2} cy={size / 2} r={radius}
                    strokeWidth={strokeWidth} fill="none" strokeLinecap="round"
                    stroke={color}
                    strokeDasharray={circumference}
                    strokeDashoffset={circumference * (1 - ratio)}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <div className="ce-ring__center">{children}</div>
        </div>
    );
}

export function SegmentedChoice({ options, value, onChange, tones = {}, ariaLabel }) {
    return (
        <div className="ce-choice" role="group" aria-label={ariaLabel}>
            {options.map((option) => (
                <button
                    key={option}
                    type="button"
                    className={"ce-choice__btn" + (value === option ? " is-active tone-" + (tones[option] || "main") : "")}
                    aria-pressed={value === option}
                    onClick={() => onChange(value === option ? "" : option)}
                >
                    {option}
                </button>
            ))}
        </div>
    );
}
